import { Injectable } from '@angular/core';
import {
  PostJourneysGetJourneyResponse,
  PostJourneysGetJourneyResponseData,
  PostJourneysGetJourneyResponseDataFlightsInner,
  PostJourneysGetJourneyResponseDataFlightsInnerOperationStatus,
  PostJourneysGetJourneyResponseDataTravelersInner,
} from 'src/sdk_checkin';
import { Router } from '@angular/router';
import { ErrorType, PageType } from '@lib/interfaces';
import { CommonLibService, PageLoadingService, AswMasterService } from '@lib/services';
import { CheckinBaseClass } from '../../components';
import { JourneyElements } from '../../interface/journey-elements';
import { CHECKIN_ROUTES } from '@conf/routes.config';
import { Contacts, JourneyInfo, PassengerStatus, SegmentStatus } from '../../interface';
import { CheckinCommonStoreService } from '../checkin-common-store/checkin-common-store.service';
import { CprResultStoreService } from '../cpr-result-store/cpr-result-store.service';
import { CheckinDeleteCompanionResultStoreService } from '../checkin-delete-companion-result-store/checkin-delete-companion-result-store.service';
import { filter, withLatestFrom } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { PREVIOUS_PAGE_IDENTIFIER } from '@conf/previous-page-identifier.config';
import { CheckinSeatmapDisplaySegmentService } from '../checkin-seatmap/checkin-seatmap-display-segment.service';
import { CheckinPassengerInputPassportService } from '../checkin-passenger-input';

@Injectable()
export class CheckinSelectService extends CheckinBaseClass {
  private cprResult$: Observable<PostJourneysGetJourneyResponse>;
  private selectSegmentIdList: string[] = [];
  private selectPassengerIdList: string[] = [];
  private checkinSelectSubscription?: Subscription;

  constructor(
    private _router: Router,
    private _commonLibService: CommonLibService,
    private _checkinCommonStoreService: CheckinCommonStoreService,
    private _cprResultStoreService: CprResultStoreService,
    private _checkinDeleteCompanionResultStoreService: CheckinDeleteCompanionResultStoreService,
    private _pageLoadingService: PageLoadingService,
    private _checkinSeatmapDisplaySegmentService: CheckinSeatmapDisplaySegmentService,
    private _checkinPassengerInputPassportService: CheckinPassengerInputPassportService,
    private _aswMasterService: AswMasterService
  ) {
    super();
    this.cprResult$ = _cprResultStoreService
      .getCprResult$()
      .pipe(filter((cprResult) => !!cprResult)) as Observable<PostJourneysGetJourneyResponse>;
    this.watchDeleteCompanionSuccess();
    this.watchDeleteCompanionFailure();
  }
  override destroy(): void {
    if (!!this.checkinSelectSubscription) {
      this.checkinSelectSubscription.unsubscribe();
      this.checkinSelectSubscription = undefined;
    }
  }

  /**
   * オンラインチェックイン処理
   * @param journeyInfo 旅程情報
   */
  public onlineCheckin(journeyInfo: JourneyInfo): void {
    if (!!this.checkinSelectSubscription) {
      this.checkinSelectSubscription.unsubscribe();
      this.checkinSelectSubscription = undefined;
    }
    // 選択したセグメント情報リストに、画面で選択したセグメント情報を設定する。
    const selectSegments = journeyInfo.segmentInfo.filter((object) => !object.isHidden && object.isChecked);
    // 選択した搭乗者情報リストに、画面で選択した搭乗者情報を設定する。
    const selectPassengers = journeyInfo.passengerInfo.filter((object) => !object.isHidden && object.isChecked);
    this.checkinSelectSubscription = this.cprResult$.subscribe((cprResult) => {
      // 搭乗者セグメント相関チェック処理
      const errorMsgId = this.passengerAndSegmentCorrelationCheck(selectSegments, selectPassengers, cprResult);
      if (errorMsgId) {
        this.setErrorMessage(errorMsgId);
        // ローディング画面表示終了
        this._pageLoadingService.endLoading();
        return;
      }
      // 選択したセグメントにメキシコ発着便を含むか判定する。
      const isSelectedMexico = selectSegments.some(
        (object) =>
          object.flightObject.departure.countryCode === 'MX' || object.flightObject.arrival.countryCode === 'MX'
      );

      // 国内単独旅程かどうか
      const isDomesticSingleTrip = selectSegments.every(
        (object) => object.flightObject.routeType === PostJourneysGetJourneyResponseDataFlightsInner.RouteTypeEnum.Dom
      );

      // メキシコ発着便ありと判定された場合、大人の搭乗者が選択されているかをCPR取得結果の18歳未満フラグで判定する。
      // 上記以外の場合（メキシコ発着便なしと判定された場合）、大人の搭乗者が選択されているかを搭乗者種別で判定する。
      const isSelectedAdult = isSelectedMexico
        ? selectPassengers.some((object) => !object.travelerObject.isUnder18)
        : selectPassengers.some((object) => ['ADT', 'B15'].includes(object.travelerObject.passengerTypeCode));

      // 選択したセグメント全てに1人でもチェックイン済みの大人がいるかどうかを判定する
      const journeyElements = cprResult.data.journeyElements as JourneyElements;
      // CPRから大人と判定される搭乗者を抽出
      // NOTE:大人かどうかの判定については、メキシコ発着便があるかどうかで判定方法を変更する
      const adultTravelers = isSelectedMexico
        ? cprResult.data.travelers.filter((object) => !object.isUnder18)
        : cprResult.data.travelers.filter((object) => ['ADT', 'B15'].includes(object.passengerTypeCode));
      const isIncludeCheckinedAdult = selectSegments.every((segments) =>
        adultTravelers.some(
          (traveler) => journeyElements[segments.flightObject.id][traveler.id]?.checkInStatus !== 'notAccepted'
        )
      );

      // 選択したセグメントにチェックイン済みの大人が存在する、もしくは選択した搭乗者情報リストに大人が存在すると判定されなかった場合、単独小児旅客かどうかを判定する。
      // 単独小児旅客である場合には、エラーを表示せずに先に進ませる。
      if (
        !isSelectedAdult &&
        !isIncludeCheckinedAdult &&
        selectPassengers.every((object) => !object.travelerObject.isUnaccompaniedMinor)
      ) {
        // メキシコ発着便ありの場合
        if (isSelectedMexico) {
          this.setErrorMessage('E0287');
          // ローディング画面表示終了
          this._pageLoadingService.endLoading();
          return;
          // メキシコ発着便なしかつ国内単独旅程以外の場合
        } else if (!isDomesticSingleTrip) {
          this.setErrorMessage('E0285');
          // ローディング画面表示終了
          this._pageLoadingService.endLoading();
          return;
          // 国内単独旅程の場合
        } else {
          this.setErrorMessage('E1871');
          // ローディング画面表示終了
          this._pageLoadingService.endLoading();
          return;
        }
      }

      // キャビンクラスが混在しているような、セグメント・搭乗者の組み合わせを選択しているか判定する
      const isCabinClassMixed = this._cprResultStoreService.isCabinClassMixed(
        selectSegments.map((selectSegment) => selectSegment.flightObject),
        selectPassengers.map((selectPassenger) => selectPassenger.travelerObject)
      );
      // キャビンクラスが混在していればエラーを表示して処理終了
      if (isCabinClassMixed) {
        this.setErrorMessage('E1058');
        // ローディング画面表示終了
        this._pageLoadingService.endLoading();
        return;
      }

      // 同伴幼児がいる搭乗者、もしくは同伴幼児のどちらかのみを選択していないかの判定
      const selectedTravelers = selectPassengers.map((passengers) => passengers.travelerObject);
      // 同行者が存在し、その同行者もチェックイン対象としている場合true、それ以外であればfalse(同行者が存在しない場合は常にtrue)
      const accompanyCheck = selectPassengers.every((passengers) => {
        const travelerInfo = passengers.travelerObject;
        const accompanyTravelerId = travelerInfo.accompanyingTravelerId;
        if (accompanyTravelerId) {
          const isSelectedAccompanyTraveler = selectedTravelers.find((traveler) => traveler.id === accompanyTravelerId);
          return !!isSelectedAccompanyTraveler;
        }
        return true;
      });
      if (!accompanyCheck) {
        this.setErrorMessage('E1023');
        // ローディング画面表示終了
        this._pageLoadingService.endLoading();
        return;
      }

      // 組み合わせ不可の搭乗者を混在させた選択をしていないかを判定する。
      if (selectPassengers.every((passenger) => passenger.travelerObject.staffCategory)) {
        // 選択した搭乗者.staffCategory=”bookable”(DUTY STAFF)の搭乗者と選択した搭乗者.staffCategory=”standby”(SUBLO旅客)の搭乗者が混在している場合
        const staffCategoryList = selectPassengers.map((passenger) => passenger.travelerObject.staffCategory);
        const isAllSameStaffCategory = staffCategoryList.every(
          (staffCategory) => staffCategory === staffCategoryList[0]
        );
        if (!isAllSameStaffCategory) {
          this.setErrorMessage('E0291');
          // ローディング画面表示終了
          this._pageLoadingService.endLoading();
          return;
        }
      } else if (selectPassengers.some((passenger) => passenger.travelerObject.staffCategory)) {
        // 選択した搭乗者情報リストについて、選択した搭乗者.staffCategoryに値がある搭乗者とない搭乗者が混在している場合
        this.setErrorMessage('E0290');
        // ローディング画面表示終了
        this._pageLoadingService.endLoading();
        return;
      }

      // チェックイン搭乗者情報入力画面スキップ判定処理呼び出し
      this.setCheckinPassengerInputSkipFlag(
        cprResult.data,
        selectPassengers,
        selectSegments.map((object) => object.flightObject.id)
      );
      // チェックインシートマップ画面スキップ判定処理呼び出し
      // NOTE:UN便についてはチェックイ対象から外すため、選択済みセグメント情報からUN便を取り除いて処理を行う
      this.setCheckinSeatmapSkipFlag(
        cprResult.data,
        selectSegments.filter(
          (segment) =>
            segment.flightObject.operationStatus.status !==
            PostJourneysGetJourneyResponseDataFlightsInnerOperationStatus.StatusEnum.Cancelled
        ),
        selectPassengers
      );

      // 画面.搭乗者情報について、未選択の搭乗者情報のidを抽出する。
      const notSelectPassengerIdList = journeyInfo.passengerInfo
        .filter((object) => !object.isChecked)
        .map((object) => object.travelerObject.id);
      this.selectSegmentIdList = selectSegments.map((object) => object.flightObject.id);
      this.selectPassengerIdList = selectPassengers.map((object) => object.travelerObject.id);
      // 未選択の搭乗者をjourneyから削除するため、以下の項目を基に同行者削除APIを呼び出す。
      if (notSelectPassengerIdList.length) {
        this._checkinDeleteCompanionResultStoreService.request({
          journeyId: this._checkinCommonStoreService.checkinCommon.journeyId!,
          travelerIds: notSelectPassengerIdList,
        });
      } else {
        // チェックイン搭乗者情報入力画面へ遷移
        this.toPassengerInput();
      }
    });
  }

  /**
   * 搭乗者セグメント相関チェック処理
   * @param selectSegments 選択されたセグメントリスト
   * @param selectPassengers 選択された搭乗者リスト
   * @param cprResult CPR取得結果
   * @returns エラーが発生した場合はエラーメッセージID、それ以外はundefinedを返却
   */
  private passengerAndSegmentCorrelationCheck(
    selectSegments: SegmentStatus[],
    selectPassengers: PassengerStatus[],
    cprResult: PostJourneysGetJourneyResponse
  ): string | undefined {
    // 選択したセグメントの1便目が他社便の場合
    if (
      selectSegments[0].flightObject.operatingType !==
      PostJourneysGetJourneyResponseDataFlightsInner.OperatingTypeEnum.NhGroupOperating
    ) {
      return 'E1320';
    }
    // 選択したセグメントの1便目がチェックイン受付時間外の場合
    if (selectSegments[0].flightObject.acceptanceStatus !== 'opened') {
      return 'E1868';
    }
    const journeyElements = cprResult.data.journeyElements as JourneyElements;
    for (let i = 0; i < selectPassengers.length; i++) {
      // 先頭セグメントフラグをtrueで生成する
      let firstSegmentFlg = true;
      // UN/TKのUN便または空席待ち便以外のセグメントリストを作成
      const checkinTargetSegments = cprResult.data.flights.filter(
        (flight) => !flight.isUnFlightInCaseOfUnTk && !flight.isWaitlistedStatus
      );
      for (let j = 0; j < checkinTargetSegments.length; j++) {
        // NOTE 現在はflight.idでの存在確認で問題ないが、空席待ちアップグレードでアップグレード後のセグメントを表示するなどがきたらbookingStatusを確認する必要がある。
        const isSelected = selectSegments.some((object) => object.flightObject.id === checkinTargetSegments[j].id);
        const isNotAccepted =
          journeyElements[checkinTargetSegments[j].id][selectPassengers[i].travelerObject.id].checkInStatus ===
          'notAccepted';
        // 当該flightsが選択したセグメント情報リストに存在しない かつ CPR取得結果.data.journeyElements.<当該flights.id>.<当該搭乗者.id>.checkInStatus=“notAccepted”(未チェックイン)の場合
        if (!isSelected && isNotAccepted) {
          this.setErrorMessage(firstSegmentFlg ? 'E0282' : 'E0284');
          return firstSegmentFlg ? 'E0282' : 'E0284';
        }
        // 当該flightsが選択したセグメント情報リストに存在する かつ CPR取得結果.data.journeyElements.<当該flights.id>.<当該搭乗者.id>.checkInStatus≠“notAccepted”(未チェックイン)の場合、選択したセグメントに対しチェックイン済/スタンバイチェックインの搭乗者を選択しているとし、エラーメッセージ(エラーメッセージID：XXX)をエラー表示用情報に追加し、搭乗者セグメント相関チェック処理を終了する。
        if (isSelected && !isNotAccepted) {
          this.setErrorMessage('E0283');
          return 'E0283';
        }
        // 当該flights.id=選択したセグメント情報リスト[0].idの場合、先頭セグメントフラグ=falseにする。
        if (checkinTargetSegments[j].id === selectSegments[0].flightObject.id) {
          firstSegmentFlg = false;
        }
        // 当該flights.id=選択したセグメント情報リストの最後のIDの場合、繰り返し処理を終了する。
        if (checkinTargetSegments[j].id === selectSegments[selectSegments.length - 1].flightObject.id) {
          break;
        }
      }
    }
    return;
  }

  /**
   * チェックイン搭乗者情報入力への画面遷移処理
   */
  private toPassengerInput(): void {
    // PNR情報取得APIスキップフラグ設定
    // 同行者追加された搭乗者のみが選択された場合true、それ以外の場合はfalseを設定
    const journeyElements = this._cprResultStoreService.cprResultData.model?.data.journeyElements as JourneyElements;
    const isGetOrderSkipped = !this.selectSegmentIdList.some((segmentId) =>
      this.selectPassengerIdList.some(
        (passengerId) =>
          journeyElements[segmentId][passengerId].orderId ===
          this._commonLibService.aswServiceStoreService.aswServiceData.orderId
      )
    );
    // チェックイン共通更新
    this._checkinCommonStoreService.updateCheckinCommon({
      selectedFlightIds: this.selectSegmentIdList,
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.CHECKIN_SELECT,
      isGetOrderSkipped: isGetOrderSkipped,
    });
    // CPR取得結果、同行者削除結果をクリアし、チェックイン搭乗者情報入力画面(C01-P030)へ遷移する。
    this._cprResultStoreService.clearCprResult();
    this._checkinDeleteCompanionResultStoreService.clearCheckinDeleteCompanionResult();
    // ローディング画面表示終了
    this._pageLoadingService.endLoading();
    this._router.navigate([CHECKIN_ROUTES.passengerInput]);
  }

  /**
   * エラーメッセージ(継続可能エラー)設定処理
   * @param errorMsgId エラーメッセージID
   */
  private setErrorMessage(errorMsgId: string): void {
    this._commonLibService.errorsHandlerService.setRetryableError(PageType.PAGE, {
      errorMsgId: errorMsgId,
    });
  }

  /**
   * 同行者削除APIレスポンス正常取得
   */
  private watchDeleteCompanionSuccess(): void {
    this.subscribeService(this._checkinDeleteCompanionResultStoreService.success$, () => {
      this.toPassengerInput();
    });
  }

  /**
   * 同行者削除APIレスポンス異常取得
   */
  private watchDeleteCompanionFailure(): void {
    this.subscribeService(
      this._checkinDeleteCompanionResultStoreService.failure$.pipe(
        withLatestFrom(this._commonLibService.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state)
      ),
      (state) => {
        if (state?.errors?.length) {
          this._commonLibService.errorsHandlerService.setNotRetryableError({
            errorType: ErrorType.SYSTEM,
            apiErrorCode: state.errors[0].code,
          });
        }
      }
    );
  }

  /**
   * チェックイン搭乗者情報入力画面をスキップするかどうかを判定し、チェクイン共通に値をセットする処理
   * チェックイン搭乗者情報入力画面のスキップ判定がtrueである場合、チェックイン共通にフラグを設定する
   * @param cprResult
   * @param selectedTravelers
   * @param selectedFlightIds
   */
  private setCheckinPassengerInputSkipFlag(
    cprResult: PostJourneysGetJourneyResponseData,
    selectedTravelers: PassengerStatus[],
    selectedFlightIds: string[]
  ) {
    const isSkipPassengerInputFlag = this.isSkipCheckinPassengerInput(cprResult, selectedTravelers, selectedFlightIds);
    if (isSkipPassengerInputFlag) {
      this._checkinCommonStoreService.updateCheckinCommon({
        isSkipCheckinPassengerInput: true,
      });
    }
  }

  /**
   * チェックイン搭乗者情報入力画面スキップ処理
   * iAPI国を含まずに、選択された搭乗者が以下の条件を全て満たす場合には、スキップ可能とする
   * ・当該搭乗者のRegulatory Checkがclearである。
   * ・当該搭乗者の搭乗者メールアドレスが登録されている。
   * ・旅程にアメリカ着のセグメントを含まない無い、または、当該搭乗者のメールアドレス(CTCE)、SMS電話番号(CTCM)、緊急連絡先(PCTC)がNHグルーブ運航便に対して登録されている。
   * @param cprResult
   * @param selectedTravelers
   * @param selectedFlightIds
   * @returns スキップできると判定された場合にはtrue、それ以外の場合にはfasle
   */
  private isSkipCheckinPassengerInput(
    cprResult: PostJourneysGetJourneyResponseData,
    selectedTravelers: PassengerStatus[],
    selectedFlightIds: string[]
  ) {
    // 国内旅程フラグの設定
    const isDomesticJourney = !cprResult.flights.some((flight) => flight?.routeType === 'INT');
    // 国内旅程以外の場合
    if (!isDomesticJourney) {
      // iAPI対象国を含む場合はスキップしない
      if (cprResult.hasFlightToFromIapiTargetCountry) {
        return false;
      }

      // CPRから選択された搭乗者を抽出して、チェック対象の搭乗者とする
      const selectedTravelerIds = selectedTravelers.map((traveler) => traveler.travelerObject.id);
      const selectedTraveler = cprResult.travelers.filter((traveler) => selectedTravelerIds.includes(traveler.id));

      // 選択された搭乗者が存在する
      if (selectedTraveler.length) {
        // NH運行便を旅程に含むかどうか
        const ngGroupOperatingCarrierList = this._aswMasterService
          .getMPropertyByKey('checkIn', 'airlines.nhGroupOperating.forAllianceBE')
          .split('|');
        const isNhGroupOperating = cprResult.flights.some((flight) =>
          ngGroupOperatingCarrierList.includes(flight.operatingAirlineCode ?? '')
        );

        // 提携自社運航便に含むかどうか
        const isPartnerOperating = cprResult.flights.some(
          (flight) =>
            flight.operatingType === PostJourneysGetJourneyResponseDataFlightsInner.OperatingTypeEnum.NhGroupOperating
        );

        // DeadHead姓リストの取得
        const deadHeadLastNames = this._commonLibService.aswMasterService
          .getMPropertyByKey('checkIn', 'passengerName.deadHeadLastNames')
          .split('|');

        // SingleName名リストの取得
        const singleNameFirstNames = this._commonLibService.aswMasterService
          .getMPropertyByKey('checkIn', 'passengerName.singleNameFirstNames')
          .split('|');

        return selectedTraveler.every((traveler) => {
          // 当該搭乗者についてRegulatory Checkがclearでない場合はその時点でスキップしない
          if (!traveler.regulatoryDetails?.statusCleared) {
            return false;
          }

          // 当該搭乗者について搭乗者メールアドレスが登録されていない場合はスキップしない
          const contacts = cprResult.contacts as Contacts;
          const emails = contacts?.[traveler.id]?.emails;
          if (traveler.passengerTypeCode !== 'INF' && !emails) {
            return false;
          }

          // アメリカ着を含む場合で、NH運行便または提携自社運航便に対して、当該搭乗者のメールアドレス(CTCE)、SMS電話番号(CTCM)、緊急連絡先(PCTC)が登録されていない場合はスキップしない
          if (cprResult.hasFlightToUs) {
            if (traveler.passengerTypeCode !== 'INF' && (isNhGroupOperating || isPartnerOperating)) {
              if (!contacts?.[traveler.id]?.ctceEmails) {
                return false;
              }
              if (!contacts?.[traveler.id]?.phones) {
                return false;
              }
              if (!contacts?.[traveler.id]?.emergencyContacts) {
                return false;
              }
            }
          }

          // チェックイン対象旅程に紐づくパスポートの抽出
          const linkedPassportsToJourney = this._checkinPassengerInputPassportService.getLinkedPassportsToJourney(
            traveler.regulatoryDetails?.passports,
            selectedFlightIds,
            true
          );
          // チェックイン対象旅程に紐づくパスポートが存在する
          if (linkedPassportsToJourney) {
            // 姓名ミドルネームの不同判定
            return linkedPassportsToJourney?.every((passport) => {
              // 当該搭乗者の姓が、DeadHead姓リストに含まれない場合
              if (!(traveler.name?.lastName && deadHeadLastNames.includes(traveler.name?.lastName))) {
                // 当該搭乗者の名が、SingleName名リストに含まれる場合
                if (traveler.name?.firstName && singleNameFirstNames.includes(traveler.name?.firstName)) {
                  // 当該搭乗者の姓が、当該パスポートの姓と一致しない場合はスキップしない
                  if (traveler.name?.lastName?.replace(/\s+/g, '') !== passport.name?.lastName?.replace(/\s+/g, '')) {
                    return false;
                  }
                  // 当該搭乗者の名が、SingleName名リストに含まれない場合
                } else {
                  // 当該搭乗者の姓が、当該パスポートの姓と一致しない場合または、当該搭乗者の名が、当該パスポートの名と一致しない場合はスキップしない
                  if (
                    traveler.name?.lastName?.replace(/\s+/g, '') !== passport.name?.lastName?.replace(/\s+/g, '') ||
                    traveler.name?.firstName?.replace(/\s+/g, '') !== passport.name?.firstName?.replace(/\s+/g, '')
                  ) {
                    return false;
                  }
                }
              }
              return true;
            });
          } else {
            return true;
          }
        });
      }

      return false;

      // 国内旅程の場合
      // 提携：UIUXs2では日本国内旅程かつ年齢・生年月日のどちらも存在しない場合はスキップ不可としていたが、提携BEではスキップ可能とする
    } else {
      // CPRから選択された搭乗者を抽出して、チェック対象の搭乗者とする
      const selectedTravelerIds = selectedTravelers.map((traveler) => traveler.travelerObject.id);
      const selectedTraveler = cprResult.travelers.filter((traveler) => selectedTravelerIds.includes(traveler.id));

      // 選択された搭乗者が存在する
      if (selectedTraveler.length) {
        return selectedTraveler.every((traveler) => {
          // 当該搭乗者について搭乗者メールアドレスが登録されていない場合はスキップしない
          const contacts = cprResult.contacts as Contacts;
          const emails = contacts?.[traveler.id]?.emails;
          if (traveler.passengerTypeCode !== 'INF' && !emails) {
            return false;
          }

          return true;
        });
      }

      return false;
    }
  }

  /**
   * チェックインシートマップ画面をスキップするかどうかを判定し、チェクイン共通に値をセットする処理
   * チェックインチェックインシートマップ画面のスキップ判定でスキップ可能である場合、チェックイン共通にフラグを設定する
   * スキップせずに表示するセグメントが存在する場合、そのセグメントのIDを表示対象セグメントIDに設定
   */
  private setCheckinSeatmapSkipFlag(
    cprResult: PostJourneysGetJourneyResponseData,
    selectedSegments: SegmentStatus[],
    selectedTravelers: PassengerStatus[]
  ) {
    const displayTargetSegments = this.extractDisplayTargetSegment(cprResult, selectedSegments, selectedTravelers);

    // 表示対象セグメントが1件もなければスキップフラグを設定、1件でもある場合は先頭の便のIDを表示対象セグメントIDに設定
    const updatedCheckinCommonState =
      displayTargetSegments.length === 0
        ? {
            isSkipCheckinSeatmap: true,
          }
        : {
            seatMapDisplayFlightId: displayTargetSegments[0].id,
          };
    this._checkinCommonStoreService.updateCheckinCommon(updatedCheckinCommonState);
  }

  /**
   * チェックインシートマップ画面スキップ処理
   * 以下のいずれかの条件を満たす場合、スキップ可能と判定する
   * ・選択したセグメントと選択した搭乗者について社員用(出張・プレジャー)かつ空席利用のセグメントが存在する
   * ・選択したセグメントについて、以下の条件で抽出し0件である
   *   ・当該セグメントがSuffix便でない
   *   ・NHグループ運航便の国際線、またはNHグループ販売便名またはNHグループ運航便名の国内線である
   *   ・当該セグメントに座席指定済みでない搭乗者が存在するまたは、CPR情報取得APIでシートマップスキップ不可と返却された
   * @param cprResult
   * @param selectedSegments
   * @param selectedTravelers
   * @returns 表示対象セグメント配列
   */
  private extractDisplayTargetSegment(
    cprResult: PostJourneysGetJourneyResponseData,
    selectedSegments: SegmentStatus[],
    selectedTravelers: PassengerStatus[]
  ): PostJourneysGetJourneyResponseDataFlightsInner[] {
    const journeyElements = cprResult.journeyElements as JourneyElements;
    // CPRから選択されたセグメントを抽出して、チェック対象のセグメントとする
    const selectedSegmentIds = selectedSegments.map((flight) => flight.flightObject.id);
    const selectedFlightsInfo = cprResult.flights.filter((flight) => selectedSegmentIds.includes(flight.id));

    // CPRから選択された搭乗者を抽出して、チェック対象の搭乗者とする
    const selectedTravelerIds = selectedTravelers.map((traveler) => traveler.travelerObject.id);
    const selectedTravelersInfo = cprResult.travelers.filter((traveler) => selectedTravelerIds.includes(traveler.id));

    // 選択したセグメントと選択した搭乗者について社員用(出張・プレジャー)かつ空席利用のセグメントが存在するかの判定
    const isAllBookable = selectedTravelersInfo.every(
      (traveler) =>
        traveler.staffCategory === PostJourneysGetJourneyResponseDataTravelersInner.StaffCategoryEnum.Bookable
    );
    const isAllStandby = selectedTravelersInfo.every(
      (traveler) =>
        traveler.staffCategory === PostJourneysGetJourneyResponseDataTravelersInner.StaffCategoryEnum.Standby
    );
    // 空席利用のセグメントステータスの取得
    const spaceAvailableStatusCode = this._commonLibService.aswMasterService
      .getMPropertyByKey('flightRelated', 'segmentStatuses.spaceAvailable')
      .split('|');

    // travelers.staffCategoryがすべて”bookable”または、すべて”standby“の場合で空席利用のセグメントが存在すればシートマップをスキップする
    if (isAllBookable || isAllStandby) {
      const isSpaceAvailableStatus = selectedFlightsInfo.some((flight) =>
        selectedTravelersInfo.some((traveler) => {
          const bookingStatusCode = journeyElements[flight.id][traveler.id].bookingStatusCode;
          // 予約ステータスコードが存在すれば、空席利用かどうかを判定して返却
          if (bookingStatusCode) {
            return spaceAvailableStatusCode.includes(bookingStatusCode);
          }
          // 予約ステータスコードそのものがなければ空席利用でない
          return false;
        })
      );
      // 空席利用が存在する場合、表示するセグメントが存在しないので、空配列を返す
      if (isSpaceAvailableStatus) {
        return [];
      }
    }

    // 座席指定対象セグメントであるセグメントについて、座席未指定の搭乗者がいるもしくはCPR情報取得APIでシートマップスキップ不可である便を抽出
    // seatmapSkippableを再度判定 元々のseatmapSkippableがtrue、もしくは席無し幼児を含まない場合にtrue、それ以外はfalse
    const seatmapSkippable =
      cprResult.seatmapSkippable || selectedTravelersInfo.every((traveler) => traveler.passengerTypeCode !== 'INF');
    return selectedFlightsInfo.filter((flight) => {
      // 運行キャリア/販売キャリアで表示対象になっているか
      const isDisplayByAirline = this._checkinSeatmapDisplaySegmentService.isDisplayTargetSegment(flight, true);
      // そのセグメントに対して1人でも座席指定がされていない搭乗者がいるか
      const isNotRegisteredSeat = selectedTravelersInfo.some(
        (traveler) => !journeyElements[flight.id][traveler.id].seat.seatNumber
      );

      return isDisplayByAirline && (isNotRegisteredSeat || !seatmapSkippable);
    });
  }
}
